// @ts-nocheck
import { useState } from 'react';
import { Stack, Title, Text as MantineText, Code, SimpleGrid, Group, Badge } from '@mantine/core';
import { Card, Avatar, Button } from '../../../components/ui';
import { mockTasks, mockProjects, getUserById, getTasksByProjectId, getProjectById } from '../mockData';

export default function TasksPage() {
  const [selectedTask, setSelectedTask] = useState(mockTasks[0]);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const filteredTasks = statusFilter
    ? mockTasks.filter((task) => task.status === statusFilter)
    : mockTasks;

  const tasksByStatus = {
    todo: mockTasks.filter((t) => t.status === 'todo'),
    'in-progress': mockTasks.filter((t) => t.status === 'in-progress'),
    review: mockTasks.filter((t) => t.status === 'review'),
    done: mockTasks.filter((t) => t.status === 'done'),
    blocked: mockTasks.filter((t) => t.status === 'blocked'),
  };

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Tasks Domain Example</Title>
        <MantineText c="dimmed" size="sm">
          Real-world example of a task management system using components with relational mock data
        </MantineText>
      </Stack>

      {/* Overview */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Tasks Overview</Title>
          <MantineText size="sm" c="dimmed">
            This example demonstrates a complete task management interface using Cards, Avatars, Badges, and relational data from our mock data system.
          </MantineText>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Key Features:</MantineText>
            <MantineText size="xs" c="dimmed" component="div">
              • 32 tasks with relational links to projects and users
              <br />
              • Status tracking (todo, in-progress, review, done, blocked)
              <br />
              • Priority levels and time estimates
              <br />
              • Assignee and reporter information
              <br />
              • Real data relationships using IDs (taskId → projectId → userId)
            </MantineText>
          </Stack>
        </Stack>
      </Card>

      {/* Task Status Summary */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Group justify="space-between">
            <Title order={4}>Task Status Summary</Title>
            <Button
              variant={statusFilter === null ? 'filled' : 'light'}
              size="sm"
              onClick={() => setStatusFilter(null)}
            >
              All Tasks ({mockTasks.length})
            </Button>
          </Group>

          <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }} spacing="md">
            <Card
              variant={statusFilter === 'todo' ? 'filled' : 'subtle'}
              p="md"
              style={{ cursor: 'pointer' }}
              onClick={() => setStatusFilter(statusFilter === 'todo' ? null : 'todo')}
            >
              <Stack gap="xs" align="center">
                <MantineText size="xl" fw={700}>
                  {tasksByStatus.todo.length}
                </MantineText>
                <Badge color="gray" variant="light">
                  To Do
                </Badge>
              </Stack>
            </Card>

            <Card
              variant={statusFilter === 'in-progress' ? 'filled' : 'subtle'}
              p="md"
              style={{ cursor: 'pointer' }}
              onClick={() => setStatusFilter(statusFilter === 'in-progress' ? null : 'in-progress')}
            >
              <Stack gap="xs" align="center">
                <MantineText size="xl" fw={700}>
                  {tasksByStatus['in-progress'].length}
                </MantineText>
                <Badge color="blue" variant="light">
                  In Progress
                </Badge>
              </Stack>
            </Card>

            <Card
              variant={statusFilter === 'review' ? 'filled' : 'subtle'}
              p="md"
              style={{ cursor: 'pointer' }}
              onClick={() => setStatusFilter(statusFilter === 'review' ? null : 'review')}
            >
              <Stack gap="xs" align="center">
                <MantineText size="xl" fw={700}>
                  {tasksByStatus.review.length}
                </MantineText>
                <Badge color="orange" variant="light">
                  Review
                </Badge>
              </Stack>
            </Card>

            <Card
              variant={statusFilter === 'done' ? 'filled' : 'subtle'}
              p="md"
              style={{ cursor: 'pointer' }}
              onClick={() => setStatusFilter(statusFilter === 'done' ? null : 'done')}
            >
              <Stack gap="xs" align="center">
                <MantineText size="xl" fw={700}>
                  {tasksByStatus.done.length}
                </MantineText>
                <Badge color="green" variant="light">
                  Done
                </Badge>
              </Stack>
            </Card>

            <Card
              variant={statusFilter === 'blocked' ? 'filled' : 'subtle'}
              p="md"
              style={{ cursor: 'pointer' }}
              onClick={() => setStatusFilter(statusFilter === 'blocked' ? null : 'blocked')}
            >
              <Stack gap="xs" align="center">
                <MantineText size="xl" fw={700}>
                  {tasksByStatus.blocked.length}
                </MantineText>
                <Badge color="red" variant="light">
                  Blocked
                </Badge>
              </Stack>
            </Card>
          </SimpleGrid>
        </Stack>
      </Card>

      {/* Task List */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Group justify="space-between">
            <Title order={4}>
              {statusFilter ? `${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} Tasks` : 'All Tasks'} (
              {filteredTasks.length})
            </Title>
            <Button variant="gradient" gradientVariant="primary" size="sm">
              + New Task
            </Button>
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
            {filteredTasks.slice(0, 12).map((task) => {
              const assignee = task.assigneeId ? getUserById(task.assigneeId) : null;
              const reporter = getUserById(task.reporterId);
              const project = getProjectById(task.projectId, mockProjects);

              return (
                <Card
                  key={task.id}
                  variant="elevated"
                  p="md"
                  hoverEffect="lift"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedTask(task)}
                >
                  <Stack gap="sm">
                    <Group justify="space-between">
                      <Badge
                        size="sm"
                        color={
                          task.status === 'done'
                            ? 'green'
                            : task.status === 'in-progress'
                            ? 'blue'
                            : task.status === 'review'
                            ? 'orange'
                            : task.status === 'blocked'
                            ? 'red'
                            : 'gray'
                        }
                      >
                        {task.status}
                      </Badge>
                      <Badge
                        size="sm"
                        variant="outline"
                        color={
                          task.priority === 'urgent'
                            ? 'red'
                            : task.priority === 'high'
                            ? 'orange'
                            : task.priority === 'medium'
                            ? 'blue'
                            : 'gray'
                        }
                      >
                        {task.priority}
                      </Badge>
                    </Group>

                    <Stack gap="xs">
                      <MantineText fw={600} lineClamp={1}>
                        {task.title}
                      </MantineText>
                      <MantineText size="xs" c="dimmed" lineClamp={2}>
                        {task.description}
                      </MantineText>
                    </Stack>

                    {project && (
                      <Badge size="xs" variant="light" color="violet">
                        {project.name}
                      </Badge>
                    )}

                    <Group justify="space-between">
                      <Stack gap={4}>
                        <MantineText size="xs" c="dimmed">
                          Assignee
                        </MantineText>
                        {assignee ? (
                          <Group gap="xs">
                            <Avatar src={assignee.avatar} size="xs" />
                            <MantineText size="xs">{assignee.name}</MantineText>
                          </Group>
                        ) : (
                          <MantineText size="xs" c="dimmed">
                            Unassigned
                          </MantineText>
                        )}
                      </Stack>

                      {task.estimatedHours && (
                        <Stack gap={4} align="flex-end">
                          <MantineText size="xs" c="dimmed">
                            Est. Hours
                          </MantineText>
                          <MantineText size="xs" fw={600}>
                            {task.estimatedHours}h
                          </MantineText>
                        </Stack>
                      )}
                    </Group>

                    {task.tags.length > 0 && (
                      <Group gap="xs">
                        {task.tags.slice(0, 2).map((tag) => (
                          <Badge key={tag} size="xs" variant="dot">
                            {tag}
                          </Badge>
                        ))}
                      </Group>
                    )}
                  </Stack>
                </Card>
              );
            })}
          </SimpleGrid>
        </Stack>
      </Card>

      {/* Selected Task Detail */}
      {selectedTask && (
        <Card withBorder p="lg">
          <Stack gap="md">
            <Title order={4}>Selected Task Details</Title>

            <Card variant="gradient" gradientVariant="secondary" p="lg">
              <Stack gap="md">
                <Group justify="space-between" align="flex-start">
                  <Stack gap="xs">
                    <MantineText c="white" fw={700} size="xl">
                      {selectedTask.title}
                    </MantineText>
                    <MantineText c="white" opacity={0.9} size="sm">
                      {selectedTask.description}
                    </MantineText>
                  </Stack>
                  <Group>
                    <Badge
                      size="lg"
                      color={
                        selectedTask.status === 'done'
                          ? 'green'
                          : selectedTask.status === 'in-progress'
                          ? 'blue'
                          : selectedTask.status === 'review'
                          ? 'orange'
                          : selectedTask.status === 'blocked'
                          ? 'red'
                          : 'gray'
                      }
                    >
                      {selectedTask.status}
                    </Badge>
                  </Group>
                </Group>

                <Group gap="lg">
                  <Stack gap={4}>
                    <MantineText c="white" opacity={0.7} size="xs">
                      Priority
                    </MantineText>
                    <MantineText c="white" fw={600} tt="capitalize">
                      {selectedTask.priority}
                    </MantineText>
                  </Stack>
                  {selectedTask.estimatedHours && (
                    <Stack gap={4}>
                      <MantineText c="white" opacity={0.7} size="xs">
                        Estimated
                      </MantineText>
                      <MantineText c="white" fw={600}>
                        {selectedTask.estimatedHours}h
                      </MantineText>
                    </Stack>
                  )}
                  {selectedTask.actualHours && (
                    <Stack gap={4}>
                      <MantineText c="white" opacity={0.7} size="xs">
                        Actual
                      </MantineText>
                      <MantineText c="white" fw={600}>
                        {selectedTask.actualHours}h
                      </MantineText>
                    </Stack>
                  )}
                  {selectedTask.dueDate && (
                    <Stack gap={4}>
                      <MantineText c="white" opacity={0.7} size="xs">
                        Due Date
                      </MantineText>
                      <MantineText c="white" fw={600}>
                        {selectedTask.dueDate.toLocaleDateString()}
                      </MantineText>
                    </Stack>
                  )}
                </Group>
              </Stack>
            </Card>

            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <Card variant="subtle" p="md">
                <Stack gap="sm">
                  <MantineText fw={600}>Assignee</MantineText>
                  {selectedTask.assigneeId && getUserById(selectedTask.assigneeId) ? (
                    <Group gap="sm">
                      <Avatar src={getUserById(selectedTask.assigneeId)?.avatar} size="md" />
                      <Stack gap={0}>
                        <MantineText size="sm" fw={500}>
                          {getUserById(selectedTask.assigneeId)?.name}
                        </MantineText>
                        <MantineText size="xs" c="dimmed">
                          {getUserById(selectedTask.assigneeId)?.title}
                        </MantineText>
                      </Stack>
                    </Group>
                  ) : (
                    <MantineText size="sm" c="dimmed">
                      Not assigned yet
                    </MantineText>
                  )}
                </Stack>
              </Card>

              <Card variant="subtle" p="md">
                <Stack gap="sm">
                  <MantineText fw={600}>Reporter</MantineText>
                  {getUserById(selectedTask.reporterId) && (
                    <Group gap="sm">
                      <Avatar src={getUserById(selectedTask.reporterId)?.avatar} size="md" />
                      <Stack gap={0}>
                        <MantineText size="sm" fw={500}>
                          {getUserById(selectedTask.reporterId)?.name}
                        </MantineText>
                        <MantineText size="xs" c="dimmed">
                          {getUserById(selectedTask.reporterId)?.title}
                        </MantineText>
                      </Stack>
                    </Group>
                  )}
                </Stack>
              </Card>
            </SimpleGrid>

            <Card variant="outline" p="md">
              <Stack gap="sm">
                <MantineText fw={600}>Project</MantineText>
                {getProjectById(selectedTask.projectId, mockProjects) && (
                  <Stack gap="xs">
                    <MantineText size="sm" fw={500}>
                      {getProjectById(selectedTask.projectId, mockProjects)?.name}
                    </MantineText>
                    <MantineText size="xs" c="dimmed">
                      {getProjectById(selectedTask.projectId, mockProjects)?.description}
                    </MantineText>
                    <Group gap="xs">
                      <Badge variant="light" color="violet">
                        {getProjectById(selectedTask.projectId, mockProjects)?.category}
                      </Badge>
                      <Badge variant="light">
                        {getProjectById(selectedTask.projectId, mockProjects)?.status}
                      </Badge>
                    </Group>
                  </Stack>
                )}
              </Stack>
            </Card>

            {selectedTask.tags.length > 0 && (
              <Card variant="outline" p="md">
                <Stack gap="xs">
                  <MantineText fw={600}>Tags</MantineText>
                  <Group gap="xs">
                    {selectedTask.tags.map((tag) => (
                      <Badge key={tag} variant="light">
                        {tag}
                      </Badge>
                    ))}
                  </Group>
                </Stack>
              </Card>
            )}
          </Stack>
        </Card>
      )}

      {/* Code Example */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Implementation Code</Title>
          <MantineText size="sm" c="dimmed">
            This example shows how to use relational mock data with UI components
          </MantineText>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Data Structure:</MantineText>
            <Code block language="typescript">
{`// Import mock data and helpers
import { mockTasks, mockProjects, getUserById, getProjectById } from './mockData';

// Access task with relational data
const task = mockTasks[0];
const assignee = getUserById(task.assigneeId); // Get assignee by ID
const reporter = getUserById(task.reporterId); // Get reporter by ID
const project = getProjectById(task.projectId, mockProjects); // Get project by ID`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Component Usage:</MantineText>
            <Code block language="tsx">
{`<Card variant="elevated" p="md">
  <Stack gap="sm">
    {/* Status and Priority */}
    <Group justify="space-between">
      <Badge color="blue">{task.status}</Badge>
      <Badge color="orange">{task.priority}</Badge>
    </Group>

    {/* Task Info */}
    <MantineText fw={600}>{task.title}</MantineText>
    <MantineText size="xs" c="dimmed">{task.description}</MantineText>

    {/* Project Badge */}
    <Badge variant="light" color="violet">
      {project.name}
    </Badge>

    {/* Assignee */}
    {assignee && (
      <Group gap="xs">
        <Avatar src={assignee.avatar} size="xs" />
        <Text size="xs">{assignee.name}</Text>
      </Group>
    )}

    {/* Tags */}
    <Group gap="xs">
      {task.tags.map((tag) => (
        <Badge key={tag} size="xs" variant="dot">{tag}</Badge>
      ))}
    </Group>
  </Stack>
</Card>`}
            </Code>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
